<template>
	<div class="actionable-button">
		<div
			class="actionable-button__unsave-container"
			v-if="editMode"
			data-test="saveAndCloseBtn"
			ref="trackedRef"
		>
			<ButtonGroup>
				<template #container>
					<Button
						className="btn--icon-only-circle"
						iconClassName="icon-unsaved"
						:iconBeforeLabel="true"
						:isLoading="loading"
						:btnClick="save"
						:disabled="!hasPendingChanges"
						data-test="saveBtn"
					/>
					<Button
						className="btn--icon-only-circle"
						iconClassName="icon-close"
						:iconBeforeLabel="true"
						:btnClick="handleEditMode"
					/>
				</template>
			</ButtonGroup>
		</div>
		<div v-else class="actionable-button__edit-container">
			<Button
				className="btn--icon-only-circle"
				iconClassName="icon-edit"
				:iconBeforeLabel="true"
				:btnClick="handleEditMode"
				data-test="editBtn"
			/>
		</div>
		<!-- v-if="displayMessage" -->
		<!-- UI/UX NOTE: the popper-position can be use if you need it -->
		<Tooltip
			v-if="displayMessage"
			class="actionable-button__tooltip"
			:variation="tagLabelLocalState.state"
			:text="tagLabelLocalState.message"
			:display="true"
			data-test="tabLabel"
		/>

		<PopperPosition
			trackedRefName="trackedRef"
			:popoverRefName="$refs.tabLabel"
			:placement="placement"
			:popperVisible="displayMessage"
			:offset="[0, 16]"
			strategy="fixed"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import PopperPosition from '@/components/visual/popper-position';
import componentPositionMixin from '@/mixins/component-position-mixin';
import Tooltip from '@/components/shells/tooltip/tooltip';
import ButtonGroup from '@/components/forms/button/button-group';

export default {
	name: 'ActionableButton',
	components: {
		Button,
		ButtonGroup,
		PopperPosition,
		Tooltip
	},
	mixins: [componentPositionMixin],
	props: {
		apiCallFn: {
			type: Function,
			default: () => {},
			require: true
		},
		hasPendingChanges: {
			type: Boolean,
			default: false
		},
		placement: {
			type: String,
			default: 'left',
			validator: value => ['left', 'right'].includes(value)
		},
		timeout: {
			type: Number,
			default: 3000
		},
		successMessage: {
			type: String,
			default: 'Success'
		},
		errorMessage: {
			type: String,
			default: 'Fail'
		}
	},
	data() {
		return {
			loading: false,
			editMode: false,
			displayMessage: false,
			tagLabelLocalState: {
				message: 'Success',
				state: 'success'
			}
		};
	},
	computed: {
		variation() {
			if (this.isMobile) return 'top';
			return 'left';
		}
	},
	methods: {
		handleEditMode() {
			this.editMode = !this.editMode;
			this.$emit('edit', this.editMode);
		},
		closeEditMode() {
			this.handleEditMode();
			this.$emit('close', true);
		},
		save() {
			this.promiseHandler();
		},
		parseResponse(message, state, response) {
			this.tagLabelLocalState = {
				message,
				state
			};
			this.$emit('save', { success: response });
		},
		promiseHandler() {
			this.loading = true;

			this.apiCallFn()
				.then(response => {
					this.parseResponse(
						this.successMessage,
						'success',
						response
					);
					this.editMode = false;
				})
				.catch(error => {
					this.parseResponse(this.errorMessage, 'error', error);
				})
				.finally(() => {
					this.displayMessage = true;
					this.loading = false;

					setTimeout(() => {
						this.displayMessage = false;
					}, this.timeout);
				});
		}
	}
};
</script>

<style lang="scss" scoped>
.actionable-button {
	display: inline-flex;
	align-items: center;

	&__unsave-container,
	&__edit-container {
		display: inline-flex;
		align-items: center;
		gap: $gutter-sm;
	}
}
</style>
